import React, { useEffect } from 'react';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { merge, uniqueId } from 'lodash';
import { useAccountOwnership } from './useAccountOwnership';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import SimpleSelect from '../../../SimpleSelect/SimpleSelectView';
import InputPercentage from '../../../InputPercentage/InputPercentageView';

export const AccountOwnership = ({
  selectedAccountType,
  ownerModel,
  accountTypes,
  updateAccount,
  accountOwners,
  clientId,
}) => {
  const {
    accountOwnersOptions,
    accountOwnersGroups,
    ownerOptions,
    allowsUnequalOwnership,
  } = useAccountOwnership({
    accountTypes,
    selectedAccountType,
    accountOwners,
  });

  const defaultOwnerModel = {
    Id: null,
    Name: '',
    OwnerType: 0,
    percent: '',
  };

  const ownerModelFields =
    ownerModel?.length > 0 ? ownerModel : [{ ...defaultOwnerModel, fieldKey: uniqueId() }];

  useEffect(() => {
    if (!allowsUnequalOwnership) {
      updateAccount(
        {
          OwnerModel: ownerModelFields.length
            ? [{ ...ownerModelFields[0], percent: '100' }]
            : [{ ...defaultOwnerModel, percent: '100' }],
        },
        clientId,
      );
    }
  }, [allowsUnequalOwnership]);

  const createOwner = () => {
    updateAccount(
      {
        OwnerModel: [...ownerModelFields, { ...defaultOwnerModel, fieldKey: uniqueId() }],
      },
      clientId,
    );
  };

  const deleteOwner = fieldKey => {
    const index = ownerModelFields.findIndex(c => c.fieldKey === fieldKey);

    updateAccount({
      OwnerModel: [...ownerModelFields.slice(0, index), ...ownerModelFields.slice(index + 1)],
    });
  };

  const updateOwner = (fieldKey, updates) => {
    const index = ownerModelFields.findIndex(c => c.fieldKey === fieldKey);

    updateAccount({
      OwnerModel: [
        ...ownerModelFields.slice(0, index),
        merge(ownerModelFields[index], updates),
        ...ownerModelFields.slice(index + 1),
      ],
    });
  };

  const distributeEqually = () => {
    const length = ownerModelFields.length;
    const baseValue = Math.floor((100 / length) * 100) / 100; // For 2 decimal places
    updateAccount({
      OwnerModel: ownerModelFields.map((owner, index) => {
        const calculatedPercentage = index === length - 1 ? 100 - baseValue * index : baseValue;
        return { ...owner, percent: calculatedPercentage.toString() };
      }),
    });
  };

  const selectedOwners = allowsUnequalOwnership
    ? ownerModelFields.length >= 2
      ? ownerModelFields.map(owner => owner.Name).concat(['Joint'])
      : ownerModelFields.map(owner => owner.Name)
    : [];

  return (
    <>
      {ownerModelFields.map((owner, index) => (
        <div key={owner.fieldKey} className="form-input contribution">
          {allowsUnequalOwnership && (
            <>
              <Box display="flex" justifyContent="space-between">
                <h2 className="list-title" style={{ marginLeft: 24, paddingBottom: 24 }}>
                  Account Ownership
                </h2>
                {index === 0 && (
                  <Button variant="link" css="margin: 16px 8px;" onClick={distributeEqually}>
                    Distribute Equally
                  </Button>
                )}
                {index === 0 && (
                  <Button
                    variant="link"
                    leftIcon="far fa-plus"
                    css="margin: 16px 8px;"
                    onClick={createOwner}
                  >
                    Add Owner
                  </Button>
                )}

                {index > 0 && (
                  <IconButton
                    variant="link"
                    icon="far fa-trash"
                    css="margin: 8px;"
                    aria-label="delete owner"
                    onClick={() => deleteOwner(owner.fieldKey)}
                  />
                )}
              </Box>
            </>
          )}

          <SimpleSelect
            placeholder={allowsUnequalOwnership ? 'Select Owner' : 'Account Owner'}
            id="adv-select-owner"
            className="no-horizontal-scroll sm"
            value={accountOwnersOptions.find(opt =>
              opt.value !== null ? opt.value === owner.Id : opt.label === owner.Name,
            )}
            options={accountOwnersOptions.filter(val => !selectedOwners.includes(val.label))}
            groups={accountOwnersGroups}
            onValueChange={opt => {
              const selectedOwnerOption = ownerOptions.find(option =>
                option.Id !== null ? option.Id === opt.value : option.Name === opt.label,
              );
              updateOwner(owner.fieldKey, {
                Id: opt.value,
                Name: opt.label,
                OwnerType: selectedOwnerOption?.OwnerType,
                percent: allowsUnequalOwnership ? owner.percent : '100',
              });
            }}
            isLeftAligned
          />
          {allowsUnequalOwnership && (
            <div className="owner-type" style={{ marginTop: 24 }}>
              <InputPercentage
                disabled={!allowsUnequalOwnership}
                placeholder="Ownership %"
                id="adv-percentage"
                maxLength="5"
                numDecimals={2}
                className="accounts"
                isLeftAligned
                value={owner.percent}
                onChange={e => {
                  updateOwner(owner.fieldKey, { percent: e.target.value });
                }}
              />
            </div>
          )}
        </div>
      ))}
    </>
  );
};
